/**
 * Клавіші переходу оболонки (keyboard.md, розд. 1) у реєстрі диспетчера (app/hotkeys.ts):
 * `G` → `H` / `D` — стартова сторінка, `G` → `T` і `T` на стартовому екрані — фокус у «Сьогодні».
 * Вікно послідовності й підказку після `G` (1,5 с) веде диспетчер; підказка показує ті клавіші
 * `G` → …, що зареєстровані зараз, з їхніми підписами.
 */
import { useNavigate } from '@tanstack/react-router';
import {
  chordKeys,
  codeLabel,
  registeredHotkeys,
  useHotkeySequence,
  useHotkeys,
} from '../app/hotkeys.ts';
import { GLOBAL } from '../app/keymap.ts';
import { keys as k } from '../i18n/uk/keys.ts';
import { today as todayText } from '../i18n/uk/today.ts';
import { Chord } from '../ui/Chord.tsx';

interface GoKeysOptions {
  onStart: boolean;
  focusToday: () => void;
  /** Підпис стартової сторінки (пункт навігації) — для підказки, шпаргалки й палітри. */
  homeLabel: string;
}

export function useGoKeys({ onStart, focusToday, homeLabel }: GoKeysOptions) {
  const navigate = useNavigate();
  const home = () => void navigate({ to: '/' });
  useHotkeys(
    [
      {
        ...GLOBAL.goHome,
        label: homeLabel,
        handler: home,
        command: { id: 'nav.home', group: 'nav' },
      },
      { ...GLOBAL.goList, handler: home },
      {
        ...GLOBAL.goToday,
        label: todayText.todayTitle,
        handler: () => (onStart ? focusToday() : home()),
      },
      ...(onStart ? [{ ...GLOBAL.today, label: k.keyFocusToday, handler: focusToday }] : []),
    ],
    { scope: 'global' },
  );
}

/** Підказка відкритої послідовності внизу ліворуч: «G → H Головна · T Сьогодні». */
export function GoHint() {
  const prefix = useHotkeySequence();
  if (!prefix) return null;
  const next = registeredHotkeys().filter(
    ({ key }) => key.prefix === prefix && key.code && key.label,
  );
  return (
    <div className="g-hint" role="status">
      <kbd>{codeLabel(prefix)}</kbd> →{' '}
      {next.map(({ key }, i) => (
        <span key={key.code}>
          {i > 0 && ' · '}
          <Chord keys={chordKeys(key).slice(1)} /> {key.label}
        </span>
      ))}
    </div>
  );
}
